import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { showSaveSuccess, showSaveError } from '@/lib/swal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { expenseClaimService } from '@/services/expenseClaimService'
import { accountManagementService } from '@/services/accountManagementService'
import type { Account } from '@/services/accountManagementService'
import type { ExpenseAccountSavePayload } from '@/api/types'

export const ExpenseClaimTypesDetail: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const isNew = params.id === 'new' || !params.id
  const editId = !isNew ? Number(params.id) : 0

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [name, setName] = useState('')
  const [debitAccount, setDebitAccount] = useState(0)
  const [creditAccount, setCreditAccount] = useState(0)
  const [vatDebitAccount, setVatDebitAccount] = useState(0)
  const [vatCreditAccount, setVatCreditAccount] = useState(0)

  useEffect(() => {
    if (!branchId) return
    let cancelled = false
    accountManagementService
      .getAllAccounts(branchId)
      .then((list) => {
        if (!cancelled) setAccounts(list || [])
      })
      .catch(() => {
        if (!cancelled) setAccounts([])
      })
    return () => {
      cancelled = true
    }
  }, [branchId])

  useEffect(() => {
    if (!branchId || isNew || !editId) return
    const load = async () => {
      try {
        setLoading(true)
        const row = await expenseClaimService.getExpenseById(editId, branchId)
        if (!row) {
          toast.error('Expense type not found.')
          navigate('/self-services/expense-claims/types')
          return
        }
        setName(row.name ?? '')
        setDebitAccount(Number(row.debitAccount) || 0)
        setCreditAccount(Number(row.creditAccount) || 0)
        setVatDebitAccount(Number(row.vatDebitAccount) || 0)
        setVatCreditAccount(Number(row.vatCreditAccount) || 0)
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? 'Failed to load expense type.')
        navigate('/self-services/expense-claims/types')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, isNew, editId, navigate])

  const accountOptions = useMemo(
    () => [
      { value: '0', label: 'Select account' },
      ...accounts.map((acc) => {
        const id = Number(acc.id || acc.accountId) || 0
        const code = acc.accountCode || ''
        const accName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${id}`
        return { value: String(id), label: code ? `${code} - ${accName}` : accName }
      }),
    ],
    [accounts]
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!branchId) return
    if (!name.trim()) {
      await showSaveError({ message: 'Expense type name is required.' })
      return
    }
    if (!debitAccount) {
      await showSaveError({ message: 'Debit Account is required.' })
      return
    }
    if (!creditAccount) {
      await showSaveError({ message: 'Credit Account is required.' })
      return
    }

    const payload: ExpenseAccountSavePayload = {
      id: isNew ? 0 : editId,
      name: name.trim(),
      debitAccount,
      creditAccount,
      vatDebitAccount,
      vatCreditAccount,
      branchId,
    }

    try {
      setSaving(true)
      if (isNew) {
        await expenseClaimService.saveExpense(payload)
        await showSaveSuccess(false)
      } else {
        await expenseClaimService.updateExpense(payload)
        await showSaveSuccess(true)
      }
      navigate('/self-services/expense-claims/types')
    } catch (err: any) {
      await showSaveError(err?.response?.data?.message ?? err?.response?.data?.result?.msg ?? err?.message ?? 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title="Expense Types" description="Expense type detail" />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Expense Types"
        description={isNew ? 'Add expense type and posting accounts' : 'Edit expense type and posting accounts'}
      />

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="p-6 space-y-6">
            {loading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 md:col-span-2">
                  <Label>Expense Type *</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Expense type name"
                  />
                </div>
                <SearchableSelect
                  label="Debit Account *"
                  options={accountOptions}
                  value={debitAccount ? String(debitAccount) : '0'}
                  onValueChange={(v) => setDebitAccount(Number(v) || 0)}
                  placeholder="Select account"
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No account found"
                />
                <SearchableSelect
                  label="Credit Account *"
                  options={accountOptions}
                  value={creditAccount ? String(creditAccount) : '0'}
                  onValueChange={(v) => setCreditAccount(Number(v) || 0)}
                  placeholder="Select account"
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No account found"
                />
                <SearchableSelect
                  label="VAT Debit Account"
                  options={accountOptions}
                  value={vatDebitAccount ? String(vatDebitAccount) : '0'}
                  onValueChange={(v) => setVatDebitAccount(Number(v) || 0)}
                  placeholder="Select account"
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No account found"
                />
                <SearchableSelect
                  label="VAT Credit Account"
                  options={accountOptions}
                  value={vatCreditAccount ? String(vatCreditAccount) : '0'}
                  onValueChange={(v) => setVatCreditAccount(Number(v) || 0)}
                  placeholder="Select account"
                  searchPlaceholder="Search accounts..."
                  emptyMessage="No account found"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={saving || loading}
            onClick={() => navigate('/self-services/expense-claims/types')}
          >
            Return
          </Button>
          <Button type="submit" disabled={saving || loading} className="ml-2">
            <Save className="h-4 w-4 mr-2" />
            {saving ? (isNew ? 'Saving...' : 'Updating...') : isNew ? 'Save' : 'Update'}
          </Button>
        </div>
      </form>
    </div>
  )
}
